import { useNavigate } from 'react-router-dom';

import { useRecoilState } from 'recoil';
import { useQueryClient } from '@tanstack/react-query';

import { updateUserPreferences } from 'api/Preferences';

import { buildTargetUserParams } from 'lib/helpers/targetUserParams';
import { userPreferencesKey } from 'lib/hooks/queries/user/useUserPreferences';

import { current_admin } from 'components/admin/lib/adminState';

/**
 * Writes one named user preference.
 *
 * With `targetUserId` the write is made on that member's behalf: the admin's own
 * `current_admin.preferences` is left alone, and the cache entry updated is the
 * target-scoped one, so an admin editing a colleague never reads back their own value.
 *
 * @param {string} key
 * @param {object} [options]
 * @param {number|string|null} [options.targetUserId]
 */
const useEditUserPreferences = (key, { targetUserId = null, targetPracticeId = null } = {}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [adminState, setAdminState] = useRecoilState(current_admin);

  const editUserPreferences = async (value) => {
    if (!targetUserId) {
      setAdminState({
        ...adminState,
        preferences: { ...adminState.preferences, [key]: value }
      });
    }

    const res = await updateUserPreferences(navigate, {
      name: key,
      value,
      ...buildTargetUserParams(targetUserId, targetPracticeId)
    });

    if (res?.code === 0) {
      const cacheKey = userPreferencesKey({ dependencies: [key], targetUserId, targetPracticeId });
      queryClient.setQueryData(cacheKey, (oldData) => {
        const newPreferences = res?.preferences;
        return {
          ...oldData,
          preferences: { ...oldData?.preferences, value: newPreferences }
        };
      });
    }

    return res;
  };

  return { adminState, editUserPreferences };
};

export default useEditUserPreferences;
